import {inject, Injectable} from '@angular/core';
import {Auth, RecaptchaVerifier, signInWithPhoneNumber} from '@angular/fire/auth';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {ApiService} from './api.service';
import {environment} from '../../../environments/environment';
import {catchError, firstValueFrom, of} from 'rxjs';
import {createLogger} from "../../shared/helpers/logger/logger";
import {RuntimeService} from "./runtime.service";
import {UserService} from "./user.service";
import {CartService} from "./cart.service";
import {GuestCartService} from "../../shared/helpers/guest-cart/guest-cart.service";

const logger = createLogger('AuthService');

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private confirmationResult: any;
    readonly #runtimeService = inject(RuntimeService);

    constructor(
        private auth: Auth,
        private apiService: ApiService,
        private http: HttpClient,
        private userService: UserService,
        private guestCartService: GuestCartService,
        private cartService: CartService
    ) {
    }

    private _recaptchaVerifier: RecaptchaVerifier | null = null;

    get recaptchaVerifier(): RecaptchaVerifier | null {
        return this._recaptchaVerifier;
    }

    async initRecaptchaVerifier(recaptchaElementId: string): Promise<void> {
        if (this.#runtimeService.isBrowser) {
            try {
                const container = document.getElementById(recaptchaElementId);
                if (!container) {
                    return;
                }
                this._recaptchaVerifier = new RecaptchaVerifier(
                    this.auth,
                    recaptchaElementId,
                    {size: 'invisible'}
                );
            } catch (error) {
                logger.error('Error initializing RecaptchaVerifier:', error);
            }
        }
    }


    // Send OTP with phone number, only works on browser
    async sendOtp(phoneNumber: string): Promise<void> {
        if (this.#runtimeService.isBrowser && this._recaptchaVerifier) {
            try {
                this.confirmationResult = await signInWithPhoneNumber(this.auth, phoneNumber, this._recaptchaVerifier);
                await this.apiService.storePhoneNumber(phoneNumber.replace(/\s+/g, ''));
            } catch (error) {
                logger.error('Phone authentication error:', error);
            }
        } else {
            logger.error('RecaptchaVerifier is not available on the server');
        }
    }

    async confirmOtp(otpCode: string): Promise<{ status: 'success', token: string } | {
        status: 'newUser',
        fid: string
    } | null> {
        if (!this.confirmationResult) {
            logger.error('No confirmation result available. OTP not sent?');
            return null;
        }
        try {
            const [userCredential] = await Promise.all([this.confirmationResult.confirm(otpCode)]);
            if (!userCredential.user) {
                logger.error('User credential is null or undefined');
                return null;
            }
            const fid = await userCredential.user.getIdToken();
            await this.apiService.storeFirebaseToken(fid);
            const token = await this.getOAuthToken(fid);
            if (token) {
                await this.userService.fetchCurrentUser(['profile']);
                const guestToken = this.guestCartService.getGuestToken();
                if (guestToken) {
                    await this.apiService.post('/cart/merge', {guest_token: guestToken});
                    await this.cartService.getCart({expand: 'cartItems,cartItems.entity,cartItems.variant'});
                    this.guestCartService.clearGuestToken();
                }
                return {status: 'success', token};
            } else {
                return {status: 'newUser', fid};
            }
        } catch (error: any) {
            logger.error('OTP confirmation error:', error);
            return null;
        }
    }

    // Sign out and clear tokens
    async signOut(): Promise<void> {
        await this.auth.signOut();
        await this.clearTokens();
        localStorage.clear(); // Clear all local storage
        await this.cartService.getCart({expand: 'cartItems,cartItems.entity,cartItems.variant'});
        this.userService.clearCurrentUser();
    }

    async getTokenFromFid(fid: string | null): Promise<string | "userNotFound" | null> {
        if (fid === null) {
            logger.warn('Something is null, early return.');
            return null;
        }
        try {
            // Check for OAuth token
            const token = await this.getOAuthToken(fid);
            if (!token) {
                logger.warn('Something was not found, user not found.');
                return 'userNotFound';
            }
            return token;
        } catch (error: any) {
            if (error?.status === 404) {
                logger.error('User not found: ', error.message);
                return 'userNotFound';
            }

            logger.error('Error fetching the token: ', error);
            return null;
        }
    }

    clearRecaptchaVerifier(): void {
        if (this._recaptchaVerifier) {
            this._recaptchaVerifier.clear();
            this._recaptchaVerifier = null;
        }
    }

    // Send Firebase ID token to your API to get OAuth2 token
    public async getOAuthToken(firebaseIdToken: string | null): Promise<string | null> {
        if (!firebaseIdToken) return null;

        const basicAuth = 'Basic ' + environment.authBasic;

        const requestBody = {fid: firebaseIdToken, scopes: 'app', grant_type: 'firebase', "redirect_uri": ""};

        const headers = new HttpHeaders({
            Authorization: basicAuth,
            'Content-Type': 'application/json',
        });

        try {
            const response: any = await firstValueFrom(
                this.http.post(`${environment.authUrl}`, requestBody, {headers}).pipe(
                    catchError((error) => {
                        logger.error('OAuth2 token error: ', error);
                        return of(null); // Return null or a fallback value
                    })
                )
            );

            if (response && response.access_token) {
                const oauthToken = response.access_token;
                const refreshToken = response.refresh_token;
                const expiresIn = response.expires_in;
                await this.apiService.setToken(oauthToken, refreshToken, expiresIn); // Store tokens securely
                return oauthToken; // Return the access token
            }

            return null;
        } catch (error) {
            logger.error('Error fetching OAuth token:', error);
            return null;
        }
    }

    // Clear tokens from IndexedDB
    private async clearTokens(): Promise<void> {
        if (this.#runtimeService.isServer) return;
        const db = await this.apiService['openIndexedDB']();
        const transaction = db.transaction('auth', 'readwrite');
        const store = transaction.objectStore('auth');
        store.clear();
        transaction.oncomplete = () => {
        };
        transaction.onerror = (error) => {
            logger.error('Error clearing tokens from IndexedDB', error);
        };
        db.close();
    }
}
